import './style.scss';

import classNames from 'classnames';
import PageDefault from 'components/PageDefault';
import PageNewsletter from 'components/PageNewsletter';
import PinchToZoom from 'components/PinchToZoom';
import ScrollHint from 'components/ScrollHint';
import SheetGesture from 'components/SheetGesture';
import SheetList from 'components/SheetList';
import PagePreviewContainer from 'containers/PagePreviewContainer';
import { ZOOM } from 'helper/js/constants';
import { compareReactNodes } from 'helper/js/helper';
import { ReferenceContext } from 'providers/Reference/index';
import { SheetContext, SheetContextProps } from 'providers/Sheet';
import { Component, ContextType, createRef, ReactNode, RefObject } from 'react';

type SheetProps = {
  handleSwipeGestures: (direction: string) => void;
  onZoomUpdate: (newZoom: number) => void;
  enableMediaLinks: boolean;
  pages: APagesItem[];
  navigate: boolean;
  animationType: string;
  currentPage: number;
  flipPages: number;
  currentVisible: number;
  isWideScreen: boolean;
  zoom: number;
  newsletterUrl?: string;
  flipToPageNumber?: number;
  recommendedFlyer?: ARecommendedFlyerItem;
  offsetHorizontal: number;
  offsetVertical: number;
  isTouchDevice: boolean;
  isAbTestVerticalScrollActive?: boolean;
  isSidebarOpen?: boolean;
  onPageSnap: (pageId: string) => void;
};
type SheetState = {
  prePages: ReactNode;
  currentPages: ReactNode;
  nextPages: ReactNode;
  isResizing: boolean;
  config: { width: number; height: number };
  flyerWrapperHeight: number;
  flyerWrapperWidth: number;
  dragging: boolean;
  cookieHeight: number;
  initialWindowInnerWidth: number;
  initialWindowOuterWidth: number;
  initialDevicePixelRatio: number;
  settledPage: number;
};

const DPI_ZOOM_THRESHOLD = 0.01;
const WINDOW_RATIO_ZOOM_THRESHOLD = 0.02;
export default class Sheet extends Component<SheetProps, SheetState> {
  static contextType = ReferenceContext;

  context!: ContextType<typeof ReferenceContext>;

  static defaultProps = {
    flipPages: 1,
    currentVisible: 1,
    isWideScreen: false,
    animationType: '',
    flipToPageNumber: null,
    offsetHorizontal: 0,
    offsetVertical: 0,
    pages: [],
    recommendedFlyer: {},
    zoom: 1,
    onZoomUpdate: () => {},
    handleSwipeGestures: () => {},
    enableMediaLinks: true,
    isAbTestVerticalScrollActive: false,
    isSidebarOpen: false,
    onPageSnap: () => {},
  };

  renderedPages = {};

  sheetContextData: SheetContextProps;

  flyerWrapperHeight = Math.floor(this.props.pages[0].height);

  flyerWrapperWidth = Math.floor(this.props.pages[0].width);

  resizeTimer?: NodeJS.Timeout = undefined;

  // <ul> scrollable container, root for IntersectionObserver
  private sheetListRef = createRef<HTMLUListElement>();

  private pageRefs = new Map<string, RefObject<HTMLLIElement>>();

  private observer: IntersectionObserver | null = null;

  private scrollTimeout: number | null = null;

  private scrollSettleTimer: number | null = null;

  private currentCenterPageId: string | null = null;

  isScrollingToPage = false;
  isScrollingFromObserver = false;

  constructor(props: SheetProps) {
    super(props);

    this.state = {
      cookieHeight: 0,
      prePages: undefined,
      currentPages: undefined,
      nextPages: undefined,
      isResizing: false,
      flyerWrapperHeight: Math.floor(props.pages[0].height),
      flyerWrapperWidth: Math.floor(props.pages[0].width),
      config: {
        width: 0,
        height: 0,
      },
      dragging: false,
      initialWindowInnerWidth: window.innerWidth,
      initialWindowOuterWidth: window.outerWidth,
      initialDevicePixelRatio: window.devicePixelRatio,
      settledPage: props.currentPage,
    };

    this.sheetContextData = {
      cCurrentVisible: props.currentVisible,
      cZoom: props.zoom,
      cSheetWidth: 0,
      cPageWidth: 0,
      cSheetHeight: 0,
      cCurrentPage: props.currentPage,
      cSettledPage: props.currentPage,
    };
  }

  shouldComponentUpdate(prevProps: SheetProps, prevState: SheetState) {
    const {
      navigate,
      currentPage,
      offsetVertical,
      offsetHorizontal,
      animationType,
      currentVisible,
      enableMediaLinks,
      flipPages,
      flipToPageNumber,
      isWideScreen,
      zoom,
      isTouchDevice,
      isAbTestVerticalScrollActive,
    } = this.props;

    return (
      navigate !== prevProps.navigate ||
      currentPage !== prevProps.currentPage ||
      offsetHorizontal !== prevProps.offsetHorizontal ||
      offsetVertical !== prevProps.offsetVertical ||
      animationType !== prevProps.animationType ||
      currentVisible !== prevProps.currentVisible ||
      enableMediaLinks !== prevProps.enableMediaLinks ||
      flipPages !== prevProps.flipPages ||
      flipToPageNumber !== prevProps.flipToPageNumber ||
      isWideScreen !== prevProps.isWideScreen ||
      zoom !== prevProps.zoom ||
      isAbTestVerticalScrollActive !== prevProps.isAbTestVerticalScrollActive ||
      this.state.isResizing !== prevState.isResizing ||
      this.state.config.height !== prevState.config.height ||
      this.state.config.width !== prevState.config.width ||
      this.state.dragging !== prevState.dragging ||
      this.state.settledPage !== prevState.settledPage ||
      isTouchDevice !== prevProps.isTouchDevice ||
      compareReactNodes(this.state.currentPages, prevState.currentPages) ||
      compareReactNodes(this.state.prePages, prevState.prePages) ||
      compareReactNodes(this.state.nextPages, prevState.nextPages)
    );
  }

  handleResize = () => {
    if (this.props.isAbTestVerticalScrollActive) {
      const isZoomed =
        typeof window !== 'undefined' && window.visualViewport
          ? window.visualViewport.scale > 1.01
          : false;
      if (isZoomed) {
        return;
      }
    }

    if (!this.state.isResizing) {
      this.setState({ isResizing: true });
    }
    this.resizeTimer && clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.setState({
        isResizing: false,
        settledPage: this.props.currentPage,
      });
      if (this.props.zoom > ZOOM.MIN) {
        this.props.onZoomUpdate(ZOOM.MIN);
      }
      if (this.props.isAbTestVerticalScrollActive) {
        const isZoomed =
          typeof window !== 'undefined' && window.visualViewport
            ? window.visualViewport.scale > 1.01
            : false;
        if (!isZoomed) {
          this.isScrollingToPage = true;
          this.scrollToPage(this.props.currentPage);
        }
      }
    }, 100);
  };

  handleOrientationChange = () => {
    this.handleResize();
    if (this.props.isAbTestVerticalScrollActive) {
      const isZoomed =
        typeof window !== 'undefined' && window.visualViewport
          ? window.visualViewport.scale > 1.01
          : false;
      if (isZoomed) return;

      this.isScrollingToPage = true;
      this.setState({ settledPage: this.props.currentPage }, () => {
        this.scrollToPage(this.props.currentPage);
        window.requestAnimationFrame(() => {
          this.scrollToPage(this.props.currentPage);
          setTimeout(() => {
            this.scrollToPage(this.props.currentPage);
          }, 150);
          setTimeout(() => {
            this.scrollToPage(this.props.currentPage);
          }, 350);
        });
      });
    }
  };

  componentDidMount() {
    this.setState({
      prePages: this.getPrePages(),
      currentPages: this.getCurrentPages(),
      nextPages: this.getNextPages(),
      initialWindowInnerWidth: window.innerWidth,
      initialWindowOuterWidth: window.outerWidth,
      initialDevicePixelRatio: window.devicePixelRatio,
    });
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleOrientationChange);

    if (this.props.isAbTestVerticalScrollActive) {
      window.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.isScrollingToPage = true;
      this.isScrollingFromObserver = false;
      const targetPage = this.props.pages.find((p) => p.number === this.props.currentPage);
      if (targetPage) {
        this.currentCenterPageId = targetPage.id;
      }
      this.scrollToPage(this.props.currentPage);
      this.setupIntersectionObserver();

      window.requestAnimationFrame(() => {
        this.scrollToPage(this.props.currentPage);

        setTimeout(() => {
          this.scrollToPage(this.props.currentPage);
        }, 150);
      });
    }
  }

  componentWillUnmount() {
    this.resizeTimer && clearTimeout(this.resizeTimer);
    if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    if (this.props.isAbTestVerticalScrollActive) {
      window.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.disconnectIntersectionObserver();
    this.pageRefs.clear();
  }

  handleVisibilityChange = () => {
    if (this.props.isAbTestVerticalScrollActive && document.visibilityState === 'visible') {
      this.isScrollingToPage = false;
      this.isScrollingFromObserver = false;
      this.setupIntersectionObserver();
      this.handleScrollSettled();
    }
  };

  static getDerivedStateFromProps(props: SheetProps, state: SheetState) {
    if (props.isAbTestVerticalScrollActive) {
      const isZoomed =
        typeof window !== 'undefined' && window.visualViewport
          ? window.visualViewport.scale > 1.01
          : false;
      if (isZoomed) {
        return null;
      }
    } else {
      // if browser zoom detected, skip recalculation
      const currentDevicePixelRatio = window.devicePixelRatio;
      if (Math.abs(currentDevicePixelRatio - state.initialDevicePixelRatio) > DPI_ZOOM_THRESHOLD) {
        return null;
      }

      // zoom detection workaround for safari (bug with devicePixelRatio)
      const currentWindowInnerWidth = window.innerWidth;
      const currentWindowOuterWidth = window.outerWidth;

      if (currentWindowInnerWidth === 0 || state.initialWindowInnerWidth === 0) {
        return null;
      }

      const currentRatio = currentWindowOuterWidth / currentWindowInnerWidth;
      const initialRatio = state.initialWindowOuterWidth / state.initialWindowInnerWidth;

      // browser zoom detected, skip recalculation
      if (Math.abs(currentRatio - initialRatio) > WINDOW_RATIO_ZOOM_THRESHOLD) {
        return null;
      }
    }

    // no zoom detected, recalculate
    const doublePageWidth = state.flyerWrapperWidth * 2;
    const { flyerWrapperHeight } = state;
    const mainContentHeight = window.innerHeight + props.offsetVertical - state.cookieHeight;
    const mainContentWidth = window.innerWidth + props.offsetHorizontal;

    const factor = props.isAbTestVerticalScrollActive
      ? (mainContentWidth / doublePageWidth) * 2
      : Math.min(
          mainContentHeight / flyerWrapperHeight,
          (mainContentWidth / doublePageWidth) * (props.currentVisible === 1 ? 2 : 1),
        );

    const newHeight = Math.floor(flyerWrapperHeight * factor);
    const newWidth = Math.floor(doublePageWidth * factor);

    if (state.config.height !== newHeight || state.config.width !== newWidth) {
      return {
        initialDevicePixelRatio: props.isAbTestVerticalScrollActive
          ? window.devicePixelRatio
          : state.initialDevicePixelRatio,
        config: {
          height: newHeight,
          width: newWidth,
        },
      };
    }

    return null;
  }

  componentDidUpdate(prevProps: SheetProps, prevState: SheetState) {
    const {
      navigate,
      flipPages,
      flipToPageNumber,
      enableMediaLinks,
      isAbTestVerticalScrollActive,
      currentPage,
      isSidebarOpen,
    } = this.props;
    if (prevState.config.height <= 0) {
      this.setState({
        prePages: this.getPrePages(),
        currentPages: this.getCurrentPages(),
        nextPages: this.getNextPages(),
      });
    }

    if (enableMediaLinks !== prevProps.enableMediaLinks) {
      this.setState({
        currentPages: this.getCurrentPages(),
      });
    }

    if (flipToPageNumber && flipToPageNumber !== prevProps.flipToPageNumber) {
      this.setState({
        prePages: this.getPrePages(flipToPageNumber),
        nextPages: this.getNextPages(flipToPageNumber),
      });
    }

    if (!navigate && (prevProps.navigate !== navigate || prevProps.flipPages !== flipPages)) {
      this.setState({
        prePages: this.getPrePages(),
        currentPages: this.getCurrentPages(),
        nextPages: this.getNextPages(),
      });
    }

    if (isAbTestVerticalScrollActive !== prevProps.isAbTestVerticalScrollActive) {
      if (isAbTestVerticalScrollActive) {
        window.addEventListener('visibilitychange', this.handleVisibilityChange);
        this.isScrollingToPage = true;
        this.isScrollingFromObserver = false;
        const targetPage = this.props.pages.find((p) => p.number === this.props.currentPage);
        if (targetPage) {
          this.currentCenterPageId = targetPage.id;
        }
        this.setState(
          {
            prePages: undefined,
            currentPages: this.getCurrentPages(),
            nextPages: undefined,
            settledPage: this.props.currentPage,
          },
          () => {
            this.scrollToPage(this.props.currentPage);
            this.setupIntersectionObserver();
            window.requestAnimationFrame(() => {
              this.scrollToPage(this.props.currentPage);
              setTimeout(() => {
                this.scrollToPage(this.props.currentPage);
              }, 150);
            });
          },
        );
      } else {
        window.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.disconnectIntersectionObserver();
        this.setState({
          prePages: this.getPrePages(),
          currentPages: this.getCurrentPages(),
          nextPages: this.getNextPages(),
        });
      }
    }

    if (this.props.currentPage !== prevProps.currentPage && !navigate) {
      if (!isAbTestVerticalScrollActive) {
        this.setState({
          currentPages: this.getCurrentPages(),
          settledPage: this.props.currentPage,
        });
      } else if (!this.isScrollingFromObserver) {
        this.setState({
          settledPage: this.props.currentPage,
        });
      }
    }

    if (
      isAbTestVerticalScrollActive &&
      !isSidebarOpen &&
      this.props.currentPage !== prevProps.currentPage
    ) {
      const isZoomed =
        typeof window !== 'undefined' && window.visualViewport
          ? window.visualViewport.scale > 1.01
          : false;
      if (!this.isScrollingFromObserver && !isZoomed) {
        this.scrollToPage(currentPage);
      }
      this.isScrollingFromObserver = false;
    }
  }

  private handleContainerScroll = () => {
    if (this.scrollSettleTimer) {
      window.clearTimeout(this.scrollSettleTimer);
    }
    // Settle debounce dedicated to triggering image downloads once scrolling is idle
    this.scrollSettleTimer = window.setTimeout(this.handleScrollSettled, 120);
  };

  private handleScrollSettled = () => {
    if (this.scrollSettleTimer) {
      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = null;
    }

    const foundPage = this.props.pages.find((p) => p.id === this.currentCenterPageId);
    const settledPageNum = foundPage ? foundPage.number : this.props.currentPage;

    if (settledPageNum && settledPageNum !== this.state.settledPage) {
      this.setState({ settledPage: settledPageNum });
    }
  };

  private setupIntersectionObserver = () => {
    const container = this.sheetListRef.current;
    if (!container || !this.props.pages?.length) return;

    this.disconnectIntersectionObserver();

    container.addEventListener('scroll', this.handleContainerScroll, { passive: true });
    container.addEventListener('scrollend', this.handleScrollSettled, { passive: true });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const pageId = (entry.target as HTMLElement).dataset.pageid;
            if (pageId) {
              this.currentCenterPageId = pageId;
              const foundPage = this.props.pages.find((p) => p.id === pageId);
              if (
                !this.isScrollingToPage &&
                foundPage &&
                foundPage.number !== this.props.currentPage
              ) {
                this.isScrollingFromObserver = true;
                this.props.onPageSnap(pageId);
              }
            }
          }
        });
      },
      {
        root: container,
        rootMargin: '-45% 0px -45% 0px',
        threshold: 0,
      },
    );

    window.requestAnimationFrame(() => {
      Array.from(container.children).forEach((child) => {
        if (child instanceof HTMLElement) {
          this.observer?.observe(child);
        }
      });
    });
  };

  private disconnectIntersectionObserver = () => {
    const container = this.sheetListRef.current;
    if (container) {
      container.removeEventListener('scroll', this.handleContainerScroll);
      container.removeEventListener('scrollend', this.handleScrollSettled);
    }
    if (this.scrollSettleTimer) {
      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  getCurrentClasses = () => {
    const {
      currentPage,
      pages,
      navigate,
      animationType,
      isWideScreen,
      isAbTestVerticalScrollActive,
    } = this.props;
    const classes = classNames({
      sheet__list: true,
      [`sheet__list--${animationType}`]: navigate,
      'sheet__list--single-page':
        ((currentPage === 1 || pages.length === currentPage) && !navigate) || !isWideScreen,
      'sheet__list--vertical-scroll': isAbTestVerticalScrollActive,
    });
    return classes;
  };

  getPrePages = (startPage?: number) => {
    const { pages, currentPage, flipPages } = this.props;
    if (this.props.isAbTestVerticalScrollActive) return;
    const start = startPage ? startPage - 1 : currentPage - 1 - flipPages;
    const end = startPage ? startPage - 1 + flipPages : currentPage - 1;
    return this.renderPages(pages.slice(start <= 0 ? 0 : start, end), 'pre');
  };

  getCurrentPages = () => {
    const { pages, currentVisible, currentPage, isAbTestVerticalScrollActive } = this.props;
    const currentPages = isAbTestVerticalScrollActive
      ? pages
      : pages.slice(currentPage - 1, currentPage - 1 + currentVisible);

    return this.renderPages(currentPages, 'current', true);
  };

  getNextPages = (startPage?: number) => {
    const { pages, currentVisible, currentPage } = this.props;
    if (this.props.isAbTestVerticalScrollActive) return;
    const start = startPage ? startPage - 1 : currentPage - 1 + currentVisible;
    const end = start + 2;
    return this.renderPages(pages.slice(start, end), 'next');
  };

  renderPageType = ({
    page,
    visible,
    className,
    isLastPage,
    pageIdForDataAttribute,
  }: {
    page: APagesItem;
    visible: boolean;
    className: string;
    isLastPage: boolean;
    pageIdForDataAttribute: string;
  }) => {
    const { newsletterUrl, recommendedFlyer } = this.props;

    let pageRef = this.pageRefs.get(pageIdForDataAttribute);
    if (!pageRef) {
      pageRef = createRef<HTMLLIElement>();
      this.pageRefs.set(pageIdForDataAttribute, pageRef);
    }

    const pageType = page.type?.toLowerCase() || page.id?.toLowerCase();

    switch (pageType) {
      case 'newsletter':
        return (
          <PageNewsletter
            pageId={page.id}
            key={page.id}
            className={className}
            visible={visible}
            newsletterUrl={newsletterUrl}
            isLastPage={isLastPage}
            ref={pageRef}
            pageWidth={this.sheetContextData.cPageWidth}
            pageHeight={this.sheetContextData.cSheetHeight}
            isAbTestVerticalScrollActive={this.props.isAbTestVerticalScrollActive}
          />
        );
      case 'recommended':
        return (
          <PagePreviewContainer
            pageId={page.id}
            key={page.id}
            className={className}
            visible={visible}
            recommendedFlyer={recommendedFlyer}
            isLastPage={isLastPage}
            ref={pageRef}
            pageWidth={this.sheetContextData.cPageWidth}
            pageHeight={this.sheetContextData.cSheetHeight}
            isAbTestVerticalScrollActive={this.props.isAbTestVerticalScrollActive}
          />
        );

      default:
        return (
          <PageDefault
            isAbTestVerticalScrollActive={this.props.isAbTestVerticalScrollActive}
            key={page.id}
            {...page}
            pageId={page.id}
            className={className}
            visible={visible}
            isLastPage={isLastPage}
            currentPage={this.props.currentPage}
            ref={pageRef}
          />
        );
    }
  };

  renderPages = (pages: APagesItem[], type: string, visible = false) =>
    pages.map((page: APagesItem, index: number) => {
      let calculatedIsLastPage: boolean;

      const pageIdToUseForDataAttribute = page.id;

      if (this.props.isAbTestVerticalScrollActive) {
        calculatedIsLastPage = this.props.pages.length === index + 1;
      } else {
        calculatedIsLastPage =
          this.props.pages.length === this.props.currentPage + index && visible;
      }

      const pageClassName = classNames(`page--${type}`, `page--${type}-${index + 1}`, {
        'page--last': calculatedIsLastPage,
      });
      return this.renderPageType({
        page,
        className: pageClassName,
        visible,
        isLastPage: calculatedIsLastPage,
        pageIdForDataAttribute: pageIdToUseForDataAttribute,
      });
    });

  handleScaleUpdated = (scale: number) => {
    if (scale !== this.props.zoom) {
      this.props.onZoomUpdate(scale);
    }
  };

  handleSwipeGestures = (direction: string) => {
    this.props.handleSwipeGestures(direction);
  };

  handlePanEvents = (position: string) => {
    this.setState({
      dragging: position === 'start',
    });
  };

  private scrollToPage = (pageNumber: number) => {
    const container = this.sheetListRef.current;
    const targetPage = this.props.pages.find((p) => p.number === pageNumber);

    if (!container || !targetPage) return;

    const targetElement = (this.pageRefs.get(targetPage.id)?.current ||
      container.querySelector(`[data-pageid="${targetPage.id}"]`)) as HTMLElement;

    if (targetElement) {
      this.isScrollingToPage = true;
      this.currentCenterPageId = targetPage.id;

      const targetY = targetElement.offsetTop;
      container.scrollTo({
        top: targetY,
        behavior: 'auto',
      });

      if (this.scrollTimeout) window.clearTimeout(this.scrollTimeout);
      this.scrollTimeout = window.setTimeout(() => {
        this.isScrollingToPage = false;
        this.handleScrollSettled();
      }, 150);
    }
  };

  getSheetContextData = () => {
    const {
      cCurrentVisible,
      cZoom,
      cSheetWidth,
      cPageWidth,
      cSheetHeight,
      cCurrentPage,
      cSettledPage,
    } = this.sheetContextData;
    const newContextData = {
      cCurrentVisible: this.props.currentVisible,
      cZoom: this.props.zoom,
      cSheetWidth: this.state.config.width,
      cPageWidth: this.state.config.width / 2,
      cSheetHeight: this.state.config.height,
      cCurrentPage: this.props.currentPage,
      cSettledPage: this.state.settledPage,
    };
    if (
      cCurrentVisible === newContextData.cCurrentVisible &&
      cZoom === newContextData.cZoom &&
      cSheetWidth === newContextData.cSheetWidth &&
      cPageWidth === newContextData.cPageWidth &&
      cSheetHeight === newContextData.cSheetHeight &&
      cCurrentPage === newContextData.cCurrentPage &&
      cSettledPage === newContextData.cSettledPage
    ) {
      return this.sheetContextData;
    }
    this.sheetContextData = newContextData;
    return this.sheetContextData;
  };

  render() {
    const { navigate, animationType, isTouchDevice, isAbTestVerticalScrollActive } = this.props;
    const { config, prePages, currentPages, nextPages, dragging } = this.state;
    const isSheetLoaded = config.width === 0 && config.height === 0;
    const sheetClassNames = classNames({
      sheet: true,
      'sheet--zooming': this.props.zoom > 1,
      'sheet--dragging': dragging,
      'sheet--disableMediaLinks': !this.props.enableMediaLinks,
      'sheet--transition': navigate,
      [`sheet--transition-${animationType}`]: navigate && animationType !== '',
      'sheet--notrans': this.state.isResizing || isSheetLoaded,
    });
    const resetZoom = () => {
      this.props.onZoomUpdate(ZOOM.MIN);
    };

    return (
      <SheetGesture
        resetZoom={resetZoom}
        swipeActive={this.props.zoom <= ZOOM.MIN}
        handleSwipeGestures={this.handleSwipeGestures}
        isAbTestVerticalScrollActive={isAbTestVerticalScrollActive}>
        <PinchToZoom
          isTouchDevice={isTouchDevice}
          onScaleUpdated={this.handleScaleUpdated}
          zoom={this.props.zoom}
          currentPage={this.props.currentPage}
          maxZoomScale={ZOOM.MAX}
          onPanEvent={this.handlePanEvents}
          isAbTestVerticalScrollActive={isAbTestVerticalScrollActive}>
          <SheetContext.Provider value={this.getSheetContextData()}>
            <div className={sheetClassNames} style={{ ...config }}>
              {!isSheetLoaded && (
                <SheetList
                  ref={this.sheetListRef}
                  prePages={prePages}
                  currentPages={currentPages}
                  nextPages={nextPages}
                  classNames={this.getCurrentClasses()}
                />
              )}
              {isAbTestVerticalScrollActive && <ScrollHint />}
            </div>
          </SheetContext.Provider>
        </PinchToZoom>
      </SheetGesture>
    );
  }
}
